import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ProductVariant {
  id: string;
  product_id: string;
  processor: string;
  ram: string;
  storage: string;
  price: number;
  compare_price: number;
  purchase_price: number;
  stock: number;
  sku: string | null;
  status: string;
}

/** Fetch all variants for a given product */
export function useProductVariants(productId: string | undefined) {
  return useQuery<ProductVariant[]>({
    queryKey: ["product-variants", productId],
    enabled: !!productId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_variants")
        .select("*")
        .eq("product_id", productId!)
        .eq("status", "active")
        .order("price", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        id: r.id,
        product_id: r.product_id,
        processor: r.processor || "",
        ram: r.ram || "",
        storage: r.storage || "",
        price: Number(r.price),
        compare_price: Number(r.compare_price),
        purchase_price: Number(r.purchase_price),
        stock: Number(r.stock),
        sku: r.sku,
        status: r.status,
      }));
    },
    staleTime: 30_000,
  });
}

/** Fetch all active variants for all products */
export function useAllProductVariants() {
  return useQuery<ProductVariant[]>({
    queryKey: ["all-product-variants"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_variants")
        .select("*")
        .eq("status", "active")
        .order("price", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        id: r.id,
        product_id: r.product_id,
        processor: r.processor || "",
        ram: r.ram || "",
        storage: r.storage || "",
        price: Number(r.price),
        compare_price: Number(r.compare_price),
        purchase_price: Number(r.purchase_price),
        stock: Number(r.stock),
        sku: r.sku,
        status: r.status,
      }));
    },
    staleTime: 30_000,
  });
}

/** Fetch ALL variants for admin (no status filter) */
export function useAdminProductVariants(productId: string | undefined) {
  return useQuery<ProductVariant[]>({
    queryKey: ["admin-product-variants", productId],
    enabled: !!productId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_variants")
        .select("*")
        .eq("product_id", productId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((r: any) => ({
        id: r.id,
        product_id: r.product_id,
        processor: r.processor || "",
        ram: r.ram || "",
        storage: r.storage || "",
        price: Number(r.price),
        compare_price: Number(r.compare_price),
        purchase_price: Number(r.purchase_price),
        stock: Number(r.stock),
        sku: r.sku,
        status: r.status,
      }));
    },
  });
}

/** Realtime subscription for variants */
export function useVariantsRealtime() {
  const qc = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("variants-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "product_variants" },
        () => {
          qc.invalidateQueries({ queryKey: ["product-variants"] });
          qc.invalidateQueries({ queryKey: ["admin-product-variants"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);
}
